/**
 * What the AI says while it designs an object, as a read-only chat: its
 * thoughts come in as messages, the newest at the bottom (followed as they
 * arrive). Shown in place of the dialog's buttons while a run is going, at a
 * fixed height (it scrolls; long lines wrap).
 */

import { SparklesIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { memo, useEffect, useRef } from 'react';
import { T } from '../../../translation/src';
import type { AiRunState } from './aiProgressTracker';

const AiMessageLog_: React.FC<{ run: AiRunState; className?: string }> = ({ run, className }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const thoughts = run.progress.thoughts;
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [thoughts.length]);

  return (
    <div ref={scrollRef} role="log" aria-live="polite" className={classNames('flex h-56 flex-col gap-2 overflow-y-auto overflow-x-hidden rounded-xl border border-gray-200 bg-[#f4f5f5] p-3 dark:border-gray-700 dark:bg-[#262626]', className)}>
      {thoughts.length === 0 ? (
        <p className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <SparklesIcon className="h-4 w-4 shrink-0 motion-safe:animate-pulse" />
          <T _str="reading your request…" swc />
        </p>
      ) : (
        thoughts.map((thought, i) => (
          <div key={i} className="flex gap-2">
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-french-violet-600 text-white dark:bg-neon-green-300 dark:text-black">
              <SparklesIcon className="h-3 w-3" />
            </span>
            <p className="min-w-0 whitespace-pre-wrap break-words rounded-2xl rounded-tl-sm bg-white px-3 py-1.5 text-sm leading-snug text-gray-700 [overflow-wrap:anywhere] dark:bg-white/5 dark:text-gray-200">{thought}</p>
          </div>
        ))
      )}
    </div>
  );
};

export const AiMessageLog = memo(AiMessageLog_);
